import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getVisitor,
  mailEventsURL,
  type SiteVisitor,
  type MailPageview,
} from "../api/mailTracking";
import Icon from "../components/icons";
import { ortamCoz, dilCoz, kaynakCoz } from "../lib/ortam";

function fmtSaat(s: string): string {
  try {
    return new Date(s).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "medium" });
  } catch {
    return s;
  }
}

// Tek ziyaretçinin gezinti akışı.
//
// Mailden gelen ziyaretçide alıcı bilgisi de görünür; anonim ziyaretçi
// yalnızca kendi anahtarıyla anılır.
export default function ZiyaretciDetay() {
  const { key = "" } = useParams();
  const navigate = useNavigate();

  const [ozet, setOzet] = useState<SiteVisitor | null>(null);
  const [sayfalar, setSayfalar] = useState<MailPageview[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [canli, setCanli] = useState(false);
  const [eskiUstte, setEskiUstte] = useState(true);

  const yukle = useCallback(async (sessiz = false) => {
    if (!key) { setErr("Geçersiz ziyaretçi"); setLoading(false); return; }
    if (!sessiz) setLoading(true);
    try {
      const d = await getVisitor(key);
      setOzet(d.visitor ?? null);
      setSayfalar(d.items);
      setErr(null);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Yükleme hatası");
    } finally {
      if (!sessiz) setLoading(false);
    }
  }, [key]);

  useEffect(() => { void yukle(); }, [yukle]);

  // Ziyaretçi gezinmeye devam ederken satırlar kendiliğinden eklenir.
  useEffect(() => {
    const es = new EventSource(mailEventsURL());
    let z: number | undefined;
    const tazele = () => {
      window.clearTimeout(z);
      z = window.setTimeout(() => void yukle(true), 800);
    };
    es.onopen = () => setCanli(true);
    es.onerror = () => setCanli(false);
    for (const t of ["open", "click", "pageview"]) es.addEventListener(t, tazele);
    return () => { window.clearTimeout(z); es.close(); };
  }, [yukle]);

  const sirali = [...sayfalar].sort((a, b) => {
    const f = new Date(a.viewed_at).getTime() - new Date(b.viewed_at).getTime();
    return eskiUstte ? f : -f;
  });

  const o = ortamCoz(ozet?.user_agent);
  const mailden = Boolean(ozet?.email);

  return (
    <div className="page">
      <div className="page-head">
        <div className="page-head-row">
          <h1>
            {mailden ? (ozet?.name || ozet?.email) : `Anonim ziyaretçi #${key.slice(0, 8)}`}
          </h1>
          <button className="btn-primary" onClick={() => navigate("/ziyaretciler")}>
            ← Ziyaretçilere dön
          </button>
        </div>
        {ozet && (
          <p className="page-sub">
            {mailden ? (
              <>
                <strong>{ozet.email}</strong> — maildeki bağlantıdan geldi
                {ozet.campaign ? ` (kampanya: ${ozet.campaign})` : ""} ·{" "}
              </>
            ) : (
              <>Maildeki bağlantıdan gelmedi; doğrudan ya da arama/başka site üzerinden ulaştı. · </>
            )}
            {ozet.page_count} sayfa · ilk görülme {fmtSaat(ozet.first_seen)} ·
            son görülme {fmtSaat(ozet.last_seen)}
            <br />
            <strong>Cihaz:</strong> {o.ozet}
            {o.vekil && " — bu bir posta sağlayıcısının görsel vekili, alıcının cihazı değil"} ·{" "}
            <strong>IP:</strong> {ozet.ip || "—"} ·{" "}
            <strong>Dil:</strong> {dilCoz(ozet.lang)} ·{" "}
            <strong>Kaynak:</strong> {kaynakCoz(ozet.referer, mailden)}
          </p>
        )}
      </div>

      <div className="toolbar">
        <span className="muted">
          {sirali.length} sayfa görüntülemesi{" "}
          <span
            className={"badge " + (canli ? "ok" : "off")}
            title={canli ? "Yeni gezinti anında düşer" : "Canlı bağlantı yok — Yenile'yi kullanın"}
          >
            {canli ? "canlı" : "bağlantı yok"}
          </span>
        </span>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn-primary" onClick={() => setEskiUstte((v) => !v)}>
            {eskiUstte ? "En yeni üstte ↓" : "En eski üstte ↑"}
          </button>
          <button className="btn-primary" onClick={() => void yukle()}>Yenile</button>
        </div>
      </div>

      {err && <div className="error">{err}</div>}

      <div className="data-table-card">
        {loading ? (
          <div className="empty-state"><div className="spinner-sm" /><p>Yükleniyor…</p></div>
        ) : sirali.length === 0 ? (
          <div className="empty-state">
            <Icon name="mail" size={34} />
            <p>Bu ziyaretçi için kayıt yok.</p>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th>Zaman</th>
                <th>Sayfa</th>
                <th>IP</th>
                <th>Cihaz / tarayıcı</th>
                <th>Dil</th>
                <th>Kaynak</th>
              </tr>
            </thead>
            <tbody>
              {sirali.map((v, i) => (
                <tr key={v.id}>
                  <td className="muted">{eskiUstte ? i + 1 : sirali.length - i}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{fmtSaat(v.viewed_at)}</td>
                  <td style={{ maxWidth: 380, wordBreak: "break-all" }}>{v.path}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{v.ip || "—"}</td>
                  <td className="muted" style={{ fontSize: 12 }} title={ortamCoz(v.user_agent).detay}>{ortamCoz(v.user_agent).ozet}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{dilCoz(v.lang)}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{kaynakCoz(v.referer, mailden)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
